import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { Skeleton } from '@/components/ui/Skeleton';
import { useResources } from './hooks/useResources';
import { useResourceFolders } from './hooks/useResourceFolders';
import { useResourceDetail } from './hooks/useResourceDetail';
import { FeaturedResourceCard } from './components/FeaturedResourceCard';
import { ResourceFoldersSection } from './components/ResourceFoldersSection';
import { RESOURCES_STRINGS } from './constants/resources-strings';
import type { ResourceItemDto } from '@/lib/types/resources.types';

/**
 * Featured resource section — shows when a featured item is in the list.
 * External links open immediately; file-type resources trigger a detail fetch
 * and open reactively once the presigned downloadUrl resolves.
 */
function FeaturedSection({ item }: { item: ResourceItemDto }) {
  const [fetchDetailId, setFetchDetailId] = useState<string | null>(null);
  const { data: detail } = useResourceDetail(fetchDetailId);

  useEffect(() => {
    if (fetchDetailId === item.id && detail?.downloadUrl) {
      window.open(detail.downloadUrl, '_blank', 'noopener,noreferrer');
      setFetchDetailId(null);
    }
  }, [detail, fetchDetailId, item.id]);

  const handleOpen = useCallback(() => {
    if (item.externalUrl) {
      window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setFetchDetailId(item.id);
  }, [item]);

  return (
    <FeaturedResourceCard
      id={item.id}
      title={item.title}
      description={item.description}
      resourceType={item.resourceType}
      createdAt={item.createdAt}
      externalUrl={item.externalUrl}
      fileUrl={item.fileUrl}
      coverImageUrl={item.coverImageUrl}trustedPartner={item.trustedPartner}
      onOpen={handleOpen}
    />
  );
}

/**
 * Member-facing Resources landing page (MYN-100 / MYN-53).
 *
 * Renders:
 * 1. Featured resource card (first item with isFeatured === true)
 * 2. The "Browse by Category" card (ResourceFoldersSection, FR-177) — the only view
 *
 * No hero banner: the client asked for the top of the page to match Events,
 * the page header and then the featured item (feedback call, 2026-10-26).
 *
 * The All Resources / Browse by Category pill tabs and the flat all-items grid
 * were removed by MYN-50/53/100 fix F-1: browsing always starts at a folder.
 * The folder grid is the page's primary content, so a failed folder fetch
 * renders a retryable ErrorState. A failed items fetch degrades quietly — it
 * only costs the optional featured card, not the page.
 */
export function ResourcesPage() {
  const { data: allItems = [], isLoading: itemsLoading } = useResources();
  const {
    data: folders = [],
    isLoading: foldersLoading,
    isError: foldersError,
    refetch: refetchFolders,
  } = useResourceFolders();

  // Kept as a predicate rather than `allItems[0]` even though `useResources`
  // asks the API for `isFeatured=true`: this is the guard that stops a
  // non-featured item rendering in the featured slot if that query ever changes
  // or the filter is dropped. It is one pass over a one-row list, not a scan.
  const featuredItem = allItems.find((item) => item.isFeatured) ?? null;

  return (
    <div className="flex min-h-full flex-col bg-bg">
      <PageHeader title={RESOURCES_STRINGS.PAGE_TITLE} />

      <div className="flex flex-col gap-8 px-page-x py-8">
        {/* 1. Featured resource */}
        {itemsLoading && <Skeleton className="h-[16rem] w-full rounded-2xl" />}
        {!itemsLoading && featuredItem && (
          <section aria-labelledby="featured-resource-heading">
            <h2 id="featured-resource-heading" className="sr-only">
              Featured Resource
            </h2>
            <FeaturedSection item={featuredItem} />
          </section>
        )}

        {/* 2. Browse by Category */}
        <ResourceFoldersSection
          folders={folders}
          isLoading={foldersLoading}
          isError={foldersError}
          onRetry={() => void refetchFolders()}
        />
      </div>
    </div>
  );
}
